/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { AdditionalParams } from './AdditionalParams';
import { QueryItem } from './query-item';
import { FiltersLazyLoadEvent } from "../filters-lazy-load-event.model";
import { SpecificationQueryParamsEventModel } from "../specification-query-params-event.model";

export class QueryParams {
  sortField: string | string[] | undefined;
  sortOrder: number | undefined;
  globalFilter: any | undefined = undefined;
  filters: FiltersLazyLoadEvent | undefined;
  rows: number | undefined;
  first: number | undefined;
  multiSortMeta: {field: string; order: number}[] | undefined;
  queryParams: Array<QueryItem> = [];
  queryParamsAsString = '&';
  additionalParams: Array<AdditionalParams> = [];

  constructor(
    {
      first,
      rows,
      sortField,
      sortOrder,
      multiSortMeta,
      filters,
      globalFilter,
    }: SpecificationQueryParamsEventModel = {},
    additionalParams: Array<AdditionalParams>
  ) {
    this.first = first;
    this.rows = rows;
    this.sortField = sortField;
    this.sortOrder = sortOrder;
    this.multiSortMeta = multiSortMeta;
    this.filters = filters;
    this.globalFilter = globalFilter;
    this.additionalParams = additionalParams;

    this.buildQueryParams();
  }

  public toString(): string {
    this.queryParamsAsString += this.queryParams.join('&');
    this.setFirstCharacter();

    return this.queryParamsAsString;
  }

  private buildQueryParams(): void {
    this.setPaginationQueryParams();
    this.setSortingQueryParams();
    this.setFilterQueryParams();
    this.setAdditionalParams();
  }

  private setAdditionalParams(): void {
    this.additionalParams.map((item) => {
      const keys = Object.getOwnPropertyNames(item);
      keys.map((key) => {
        this.queryParams.push(new QueryItem(key, item[key]));
      });
    });
  }

  private setFirstCharacter(): void {
    this.queryParamsAsString = this.queryParamsAsString.startsWith('&')
      ? this.queryParamsAsString.replace('&', '?')
      : `?${this.queryParamsAsString}`;
  }

  private setPaginationQueryParams(): void {
    if (this.first !== null && this.first !== undefined && this.rows) {
      this.queryParams.push(new QueryItem(`page`, this.first / this.rows));
      this.queryParams.push(new QueryItem(`size`, this.rows));
    }
  }

  private setSortingQueryParams(): void {
    const primengSortList = this.multiSortMeta
      ? this.multiSortMeta
      : [
          {
            field: this.sortField,
            order: this.sortOrder,
          },
        ];

    primengSortList?.map((value) => {
      if (value?.field) {
        this.queryParams.push(
          new QueryItem(`sort`, `${value?.field},${Order[value?.order ?? 1]}`)
        );
      }
    });
  }

  private setFilterQueryParams(): void {
    // ToDo: implement  globalFilter;
    // ToDo: refactor;

    for (const key in this.filters) {
      const complexFilter = this.filters[key].value;

      if (
        complexFilter instanceof Object &&
        (complexFilter.min || complexFilter.max)
      ) {
        if (complexFilter.min) {
          this.queryParams.push(
            new QueryItem(
              `filter[${key}][${FilterMatchMode['gt']}]`,
              complexFilter.min
            )
          );
        }
        if (complexFilter.max) {
          this.queryParams.push(
            new QueryItem(
              `filter[${key}][${FilterMatchMode['lt']}]`,
              complexFilter.max
            )
          );
        }
      } else if (
        complexFilter instanceof Object &&
        (complexFilter.startDate || complexFilter.endDate)
      ) {
        if (complexFilter.startDate) {
          this.queryParams.push(
            new QueryItem(
              `filter[${key}][${FilterMatchMode['gt']}]`,
              complexFilter.startDate
            )
          );
        }
        if (complexFilter.endDate) {
          this.queryParams.push(
            new QueryItem(
              `filter[${key}][${FilterMatchMode['lt']}]`,
              complexFilter.endDate
            )
          );
        }
      } else {
        const matchModeKey = this.filters[key]?.matchMode ?? 'contains';
        const matchMode = FilterMatchMode[matchModeKey as keyof typeof FilterMatchMode] ?? FilterMatchMode['contains'];


        if (Array.isArray(this.filters[key].value)) {
          this.filters[key].value.map((item: any) => {
            this.queryParams.push(
              new QueryItem(`filter[${key}][${matchMode}]`, item)
            );
          });
        } else {
          this.queryParams.push(
            new QueryItem(
              `filter[${key}][${matchMode}]`,
              this.filters[key].value
            )
          );
        }
      }
    }
  }
}

enum Order {
  desc = -1,
  asc = 1,
}

enum FilterMatchMode {
  startsWith = 'SW',
  contains = 'LIKE',
  endsWith = 'EW',
  equals = 'EQ',
  in = 'IN',
  lt = 'LT',
  gt = 'GT',
  FTS = 'FTS'
}
